"use client";

import React from "react";
import { motion } from "framer-motion";
import { CircleCheck as CheckCircle2, ClipboardList, Calculator, Wrench, ShieldCheck, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const inclusions = [{
  title: "In-Home Diagnosis",
  icon: <ClipboardList className="size-5 text-primary" />
}, {
  title: "Replacement Parts",
  icon: <Wrench className="size-5 text-primary" />
}, {
  title: "Pro Quote First",
  icon: <ShieldCheck className="size-5 text-primary" />
}, {
  title: "Work Area Cleanup",
  icon: <CheckCircle2 className="size-5 text-primary" />
}];
const processSteps = [{
  step: "01",
  title: "Request Match",
  description: "Submit details about your appliance and symptoms to connect with an available local pro."
}, {
  step: "02",
  title: "Diagnostic Visit",
  description: "Your assigned local technician inspects the appliance to identify the mechanical or electrical root cause."
}, {
  step: "03",
  title: "Written Estimate",
  description: "The pro gives their own quote detailing parts and labor before any repair work starts."
}, {
  step: "04",
  title: "Testing & Completion",
  description: "The technician completes the repair, tests appliance cycle operation, and leaves the workspace clean."
}];
const pricingData = [{
  service: "Diagnostic Evaluation",
  price: "Varies by pro",
  note: "quoted when scheduling your service visit"
}, {
  service: "Appliance Labor",
  price: "Pro estimate",
  note: "based on job complexity and component access"
}, {
  service: "Replacement Parts",
  price: "Itemized quote",
  note: "specific to appliance model and manufacturer part"
}, {
  service: "Additional Units",
  price: "Custom quote",
  note: "ask your local technician about multiple appliance visits"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function ServiceDetails() {
  return <section id="inclusionsprocesspricing" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4">Service Details</Badge>
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Transparent Process, <span className="text-primary">Clear Terms</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            We connect you with experienced trade pros who believe in clear communication, direct estimates, and getting your appliances back to working order.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          
          {}
          <div className="lg:col-span-7 space-y-12">
            
            {}
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true
          }}>
              <h3 className="text-2xl font-semibold font-default text-foreground mb-6 flex items-center gap-2">
                <CheckCircle2 className="size-6 text-primary" />
                What's Included
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {inclusions.map((item, i) => <motion.div key={i} data-index={i} variants={itemVariants} className="flex items-center gap-4 p-4 rounded-lg border border-border bg-card/50 shadow-sm">
                    <div className="shrink-0 bg-background p-2 rounded-md shadow-xs border border-border/50">
                      {item.icon}
                    </div>
                    <span className="font-medium text-foreground font-default">{item.title}</span>
                  </motion.div>)}
              </div>
            </motion.div>

            {}
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true
          }}>
              <h3 className="text-2xl font-semibold font-default text-foreground mb-8 flex items-center gap-2">
                <Calculator className="size-6 text-primary" />
                Our Process
              </h3>
              <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-border before:to-transparent">
                {processSteps.map((step, i) => <motion.div key={i} data-index={i} variants={itemVariants} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg border-4 border-background bg-primary text-primary-foreground font-bold text-sm shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10">
                      {step.step}
                    </div>
                    <Card className="w-[calc(100%-3rem)] md:w-[calc(50%-2.5rem)] shadow-sm hover:shadow-md transition-shadow duration-300">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-default text-foreground">{step.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground text-sm">{step.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>)}
              </div>
            </motion.div>

          </div>

          {}
          <motion.div className="lg:col-span-5" initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }}>
            <div className="sticky top-24">
              <Card className="border-primary/20 shadow-lg overflow-hidden">
                <div className="bg-primary/5 p-6 border-b border-border">
                  <h3 className="text-2xl font-semibold font-default text-foreground mb-2">Cost Information</h3>
                  <p className="text-muted-foreground text-sm">Costs vary by appliance, part and pro. The pro gives their own quote before work begins.</p>
                </div>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader className="bg-muted/30">
                      <TableRow>
                        <TableHead className="w-[50%] font-default text-foreground">Service Item</TableHead>
                        <TableHead className="text-right font-default text-foreground">Pricing Policy</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pricingData.map((item, i) => <TableRow key={i} data-index={i} className="group">
                          <TableCell className="font-medium text-foreground">
                            <div className="flex flex-col">
                              <span className="font-default">{item.service}</span>
                              <span className="text-xs text-muted-foreground font-normal mt-0.5">{item.note}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-foreground font-default">
                            {item.price}
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </CardContent>
                <div className="p-6 bg-muted/10 border-t border-border flex flex-col gap-4">
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                    <p>Independent technicians provide their own quotes and service policies prior to starting repairs. Always verify scope and details with your technician.</p>
                  </div>
                  <Button asChild size="lg" className="w-full mt-2">
                    <Link to="#bookingrequest">
                      Request a Quote
                      <ArrowRight className="ml-2 size-4" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}